/**
 * nh-store-locator.js
 *
 * Country tab switching, search filtering, geolocation auto-detection,
 * and Google Maps pin rendering for the NH store locator page.
 */

(function() {
  'use strict';

  // ── Section ID (WCAG 4.1.1: unique IDs when section is reused) ──────────

  var sectionEl = document.querySelector('.nh-locator[data-section-id]');
  if (!sectionEl) return;
  var sectionId = sectionEl.dataset.sectionId;

  // ── Data ──────────────────────────────────────────────────────────────────

  var dataEl = document.getElementById('nh-locator-data-' + sectionId);
  if (!dataEl) return;

  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (_) { return; }

  var apiKey    = data.apiKey || '';
  var locations = data.locations || [];

  // ── Country config ────────────────────────────────────────────────────────
  //
  // 2026-09-09: this file used to keep its own hardcoded list of four
  // countries (countryMap) plus hardcoded map centres (slugToCenter), so a
  // store in any other country got no map pin and no tab — Gomez Y Molina in
  // Spain was invisible. Both are gone. The country registry now lives in
  // sections/nh-store-locator.liquid and arrives here in the JSON data block,
  // and each location already carries its resolved `slug`, so there is no
  // country list in this file to keep in sync.

  var countries = data.countries || [];

  var configBySlug = {};
  countries.forEach(function(c) {
    if (c && c.slug) configBySlug[c.slug] = c;
  });

  function configFor(loc) {
    return configBySlug[(loc && loc.slug) || ''] || {};
  }

  // ── Localized string templates from the section dataset ───────────────────
  //
  // Values are passed from Liquid with literal __TOKENS__ so this file never
  // has to know the sentence structure of any language.

  function template(name, fallback, tokens) {
    var s = sectionEl.dataset[name] || fallback;
    Object.keys(tokens || {}).forEach(function(k) {
      s = s.split(k).join(String(tokens[k]));
    });
    return s;
  }

  // ── Tab switching ─────────────────────────────────────────────────────────

  var tabs   = document.querySelectorAll('[data-nh-tab]');
  var panels = document.querySelectorAll('.nh-locator__panel');

  function activateTab(slug) {
    tabs.forEach(function(tab) {
      var isActive = tab.dataset.nhTab === slug;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    panels.forEach(function(panel) {
      var isActive = panel.id === 'nh-locator-panel-' + slug + '-' + sectionId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
    // Clear search when switching tabs
    var searchInput = document.querySelector('[data-nh-locator-search]');
    if (searchInput) searchInput.value = '';
    showAllCardsInActivePanel();

    // Hide map and search for online retailers tab
    var mapWrap = document.querySelector('.nh-locator__map-wrap');
    var searchWrap = document.querySelector('.nh-locator__search-wrap');
    var isOnline = slug === 'online';
    if (mapWrap) mapWrap.style.display = isOnline ? 'none' : '';
    if (searchWrap) searchWrap.style.display = isOnline ? 'none' : '';

    // Re-center map (only for physical location tabs)
    if (window._nhMap && !isOnline) centerMap(slug);
  }

  tabs.forEach(function(tab) {
    tab.addEventListener('click', function() {
      activateTab(tab.dataset.nhTab);
    });
  });

  // ── WCAG 2.1.1: Keyboard arrow navigation for tabs ────────────────────────

  var tabList = document.querySelector('.nh-locator__tabs');
  if (tabList) {
    tabList.addEventListener('keydown', function(e) {
      var visibleTabs = Array.prototype.slice.call(tabs).filter(function(t) {
        return t.offsetParent !== null;
      });
      var currentIndex = visibleTabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      var nextIndex;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % visibleTabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = visibleTabs.length - 1;
      } else {
        return;
      }

      visibleTabs[nextIndex].focus();
      activateTab(visibleTabs[nextIndex].dataset.nhTab);
    });
  }

  // ── Mobile dropdown ────────────────────────────────────────────────────────

  var countrySelect = document.querySelector('[data-nh-country-select]');
  if (countrySelect) {
    countrySelect.addEventListener('change', function() {
      activateTab(countrySelect.value);
    });
  }

  // Sync dropdown when activateTab is called (e.g. from auto-detect)
  var _originalActivateTab = activateTab;
  activateTab = function(slug) {
    _originalActivateTab(slug);
    if (countrySelect && countrySelect.value !== slug) {
      countrySelect.value = slug;
    }
  };

  // ── Store original count text from Liquid (before JS modifies it) ────────
  document.querySelectorAll('[data-nh-count]').forEach(function(el) {
    el.dataset.originalText = el.textContent.trim();
  });

  // ── Search filtering ──────────────────────────────────────────────────────

  var searchInput = document.querySelector('[data-nh-locator-search]');
  if (searchInput) {
    var debounceTimer = null;
    searchInput.addEventListener('input', function() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        var query = searchInput.value.trim();
        if (!query) {
          showAllCardsInActivePanel();
          // Reset the map to the active country's frame
          var activeTab = document.querySelector('.nh-locator__tab.is-active');
          if (activeTab && window._nhMap) centerMap(activeTab.dataset.nhTab);
          return;
        }
        // Reveal all deferred cards so search results are never hidden
        revealDeferredCards();
        // Try text match first
        var textMatches = filterCardsByText(query.toLowerCase());
        if (textMatches > 0) {
          // Frame the map around the matches (e.g. typing "Miami")
          fitMapToVisible(null);
        } else if (window.google && window.google.maps && window.google.maps.Geocoder) {
          // No text matches — try proximity search around the geocoded place
          geocodeAndFilter(query);
        }
      }, 300);
    });
  }

  function showAllCardsInActivePanel() {
    var activePanel = document.querySelector('.nh-locator__panel.is-active');
    if (!activePanel) return;
    activePanel.querySelectorAll('[data-nh-card]').forEach(function(card) {
      card.style.display = card.classList.contains('nh-locator__card--deferred') ? 'none' : '';
      card.style.order = '';
    });
    // Restore original count text from Liquid
    var countEl = activePanel.querySelector('[data-nh-count]');
    if (countEl && countEl.dataset.originalText) {
      countEl.textContent = countEl.dataset.originalText;
    }
  }

  /**
   * Reveals all deferred (hidden via "View more") cards in the active panel
   * and removes the "View more" button. Called when the user starts searching
   * so that search results are never hidden behind progressive disclosure.
   */
  function revealDeferredCards() {
    var activePanel = document.querySelector('.nh-locator__panel.is-active');
    if (!activePanel) return;
    activePanel.querySelectorAll('.nh-locator__card--deferred').forEach(function(card) {
      card.classList.remove('nh-locator__card--deferred');
      card.style.display = '';
    });
    var btn = activePanel.querySelector('.nh-locator__view-more');
    if (btn) btn.remove();
  }

  // ── US state name → abbreviation mapping ────────────────────────────────

  var stateNames = {
    'alabama':'al','alaska':'ak','arizona':'az','arkansas':'ar','california':'ca',
    'colorado':'co','connecticut':'ct','delaware':'de','florida':'fl','georgia':'ga',
    'hawaii':'hi','idaho':'id','illinois':'il','indiana':'in','iowa':'ia',
    'kansas':'ks','kentucky':'ky','louisiana':'la','maine':'me','maryland':'md',
    'massachusetts':'ma','michigan':'mi','minnesota':'mn','mississippi':'ms',
    'missouri':'mo','montana':'mt','nebraska':'ne','nevada':'nv','new hampshire':'nh',
    'new jersey':'nj','new mexico':'nm','new york':'ny','north carolina':'nc',
    'north dakota':'nd','ohio':'oh','oklahoma':'ok','oregon':'or','pennsylvania':'pa',
    'rhode island':'ri','south carolina':'sc','south dakota':'sd','tennessee':'tn',
    'texas':'tx','utah':'ut','vermont':'vt','virginia':'va','washington':'wa',
    'west virginia':'wv','wisconsin':'wi','wyoming':'wy'
  };

  function filterCardsByText(query) {
    var activePanel = document.querySelector('.nh-locator__panel.is-active');
    if (!activePanel) return 0;

    var cards = activePanel.querySelectorAll('[data-nh-card]');
    var visibleCount = 0;

    // Expand state name to abbreviation for matching
    var stateAbbr = stateNames[query] || '';

    cards.forEach(function(card) {
      var city  = (card.dataset.city  || '').toLowerCase();
      var state = (card.dataset.state || '').toLowerCase();
      var zip   = (card.dataset.zip   || '').toLowerCase();
      var name  = (card.querySelector('.nh-locator__card-name') || {}).textContent || '';
      name = name.toLowerCase();

      var match = city.indexOf(query) !== -1
               || state.indexOf(query) !== -1
               || zip.indexOf(query) !== -1
               || name.indexOf(query) !== -1;

      // Also match full state name → abbreviation
      if (!match && stateAbbr && state === stateAbbr) {
        match = true;
      }

      card.style.display = match ? '' : 'none';
      card.style.order = '';
      if (match) visibleCount++;
    });

    updateCount(activePanel, visibleCount);
    return visibleCount;
  }

  function geocodeAndFilter(query) {
    var geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: query }, function(results, status) {
      if (status !== 'OK' || !results || !results[0]) return;

      var loc = results[0].geometry.location;
      var userLat = loc.lat();
      var userLng = loc.lng();

      var activePanel = document.querySelector('.nh-locator__panel.is-active');
      if (!activePanel) return;

      var cards = activePanel.querySelectorAll('[data-nh-card]');
      var cardDistances = [];

      cards.forEach(function(card) {
        var lat = parseFloat(card.dataset.lat);
        var lng = parseFloat(card.dataset.lng);
        if (isNaN(lat) || isNaN(lng)) {
          card.style.display = 'none';
          return;
        }
        var dist = haversine(userLat, userLng, lat, lng);
        cardDistances.push({ card: card, distance: dist });
      });

      // Sort by distance
      cardDistances.sort(function(a, b) { return a.distance - b.distance; });

      // Show stores within 80km — and never show nothing: when the area
      // has no boutique in range, the closest one is shown instead
      // (2026-07 review: "no locations found" is a dead end).
      var visibleCount = 0;
      cardDistances.forEach(function(item, index) {
        if (item.distance <= 80) {
          item.card.style.display = '';
          item.card.style.order = index;
          visibleCount++;
        } else {
          item.card.style.display = 'none';
        }
      });

      var closestFallback = false;
      if (visibleCount === 0 && cardDistances.length > 0) {
        cardDistances[0].card.style.display = '';
        cardDistances[0].card.style.order = 0;
        visibleCount = 1;
        closestFallback = true;
      }

      // Update count (localized templates from the section dataset)
      var countEl = activePanel.querySelector('[data-nh-count]');
      if (countEl) {
        if (closestFallback) {
          countEl.textContent = template('countClosest', 'Closest boutique · __DISTANCE__ km away', {
            '__DISTANCE__': Math.round(cardDistances[0].distance)
          });
        } else {
          countEl.textContent = template('countNearest', '__COUNT__ nearest locations', {
            '__COUNT__': visibleCount
          });
        }
      }

      // Frame the map around the searched place and the shown boutiques —
      // typing "Miami" centres on Miami even when the nearest boutique is
      // hundreds of km away.
      fitMapToVisible({ lat: userLat, lng: userLng });
    });
  }

  /**
   * Fits the map viewport around the currently visible store cards in the
   * active panel, optionally including an extra point (the geocoded search
   * location). Single point: gentle city-level zoom. Multiple points:
   * fitBounds with padding, capped so one downtown boutique never renders
   * at street level.
   */
  function fitMapToVisible(extraPoint) {
    if (!window._nhMap || !window.google) return;
    var activePanel = document.querySelector('.nh-locator__panel.is-active');
    if (!activePanel) return;

    var bounds = new google.maps.LatLngBounds();
    var points = 0;

    activePanel.querySelectorAll('[data-nh-card]').forEach(function(card) {
      if (card.style.display === 'none') return;
      var lat = parseFloat(card.dataset.lat);
      var lng = parseFloat(card.dataset.lng);
      if (isNaN(lat) || isNaN(lng)) return;
      bounds.extend({ lat: lat, lng: lng });
      points++;
    });

    if (extraPoint && typeof extraPoint.lat === 'number' && typeof extraPoint.lng === 'number') {
      bounds.extend(extraPoint);
      points++;
    }

    if (points === 0) return;

    if (points === 1) {
      window._nhMap.setCenter(bounds.getCenter());
      window._nhMap.setZoom(11);
      return;
    }

    window._nhMap.fitBounds(bounds, 48);
    // Cap the zoom after fitBounds settles (two nearby pins otherwise
    // land at street level).
    google.maps.event.addListenerOnce(window._nhMap, 'idle', function() {
      if (window._nhMap.getZoom() > 13) window._nhMap.setZoom(13);
    });
  }

  function updateCount(panel, count) {
    var countEl = panel.querySelector('[data-nh-count]');
    if (!countEl) return;
    // Previously hardcoded English ("N locations in X"), which showed through
    // on the French page. Now uses the localized template from Liquid.
    countEl.textContent = template('locationsCount', '__COUNT__ locations in __COUNTRY__', {
      '__COUNT__': count,
      '__COUNTRY__': panel.dataset.countryName || ''
    });
  }

  // ── Auto-detect country — Shopify market first, then geolocation ────────

  function tabExists(slug) {
    return !!(slug && document.querySelector('[data-nh-tab="' + slug + '"]'));
  }

  function slugForIso(iso) {
    for (var i = 0; i < countries.length; i++) {
      if (countries[i].iso && countries[i].iso.toUpperCase() === iso) {
        return countries[i].slug;
      }
    }
    return null;
  }

  function autoDetectCountry() {
    // 1. Shopify market / country (no cookies required).
    var shopifyCountry = '';
    if (window.Shopify && window.Shopify.country) {
      shopifyCountry = window.Shopify.country.toUpperCase();
    }

    if (shopifyCountry) {
      // An exact match on a country we actually stock always wins. This is
      // what fixes Spain: 'ES' used to be hardcoded to the France tab, so a
      // visitor in Madrid was shown Paris even once a Spanish store existed.
      var exact = slugForIso(shopifyCountry);
      if (tabExists(exact)) {
        activateTab(exact);
        return;
      }

      // Otherwise fall back to the nearest market we do stock. Only for
      // countries with no store of their own; as soon as one is added, the
      // exact match above takes over and the entry here becomes dead weight.
      var regionFallback = {
        'CA': 'united-states',
        'DE': 'france', 'IT': 'france', 'BE': 'france', 'NL': 'france',
        'LU': 'france', 'AT': 'france', 'PT': 'spain'
      };
      var fallbackSlug = regionFallback[shopifyCountry];
      if (tabExists(fallbackSlug)) {
        activateTab(fallbackSlug);
        return;
      }
    }

    // 2. Currency, as a weaker signal than the market.
    var currency = '';
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currency = window.Shopify.currency.active.toUpperCase();
    }
    var currencySlugMap = {
      'USD': 'united-states',
      'EUR': 'france',
      'CHF': 'switzerland',
      'HKD': 'hong-kong'
    };
    if (currency && tabExists(currencySlugMap[currency])) {
      activateTab(currencySlugMap[currency]);
      return;
    }

    // 3. Page language.
    var htmlLang = document.documentElement.lang || '';
    if (htmlLang.indexOf('fr') === 0 && tabExists('france')) {
      activateTab('france');
      return;
    }

    // 4. Fall back to browser geolocation (requires permission)
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      function(pos) {
        var closest = findClosestCountry(pos.coords.latitude, pos.coords.longitude);
        if (tabExists(closest)) activateTab(closest);
      },
      function() {
        // Permission denied or error — stay on default tab
      },
      { timeout: 5000, maximumAge: 300000 }
    );
  }

  function findClosestCountry(lat, lng) {
    var minDist = Infinity;
    var bestSlug = null;

    locations.forEach(function(loc) {
      if (!loc.slug || !loc.lat || !loc.lng) return;
      var d = haversine(lat, lng, loc.lat, loc.lng);
      if (d < minDist) {
        minDist = d;
        bestSlug = loc.slug;
      }
    });

    return bestSlug;
  }

  function haversine(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
          + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
          * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Try geolocation on load
  autoDetectCountry();

  // ── Google Maps ───────────────────────────────────────────────────────────

  function initMap() {
    if (!apiKey) return;

    var script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&callback=_nhMapReady';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  // ── Auth failure (expired, invalid or referrer-blocked key) ─────────────
  //
  // 2026-10-07: Google calls window.gm_authFailure when the key is rejected
  // and paints its own "Oops! Something went wrong" UI inside the container.
  // The theme's global img rule blew that icon up to a giant grey blob
  // (reported by Mathilde and Maria on the FR page, where Translate & Adapt
  // still held the old key as a French "translation" of the setting).
  // Replace Google's UI with the theme placeholder, drop the markers, and
  // let the store cards below carry the information.
  window.gm_authFailure = function() {
    var mapEl = document.getElementById('nh-locator-map-' + sectionId);
    if (!mapEl) return;
    console.error('[nh-store-locator] Google Maps rejected the API key on ' + location.pathname + '. Check the section setting and any Translate & Adapt override of it for this locale.');
    window._nhMap = null;
    window._nhMarkers = [];
    mapEl.innerHTML = '';
    var placeholder = document.createElement('div');
    placeholder.className = 'nh-locator__map-placeholder';
    var msg = document.createElement('p');
    msg.textContent = sectionEl.dataset.mapUnavailable || 'The map is temporarily unavailable. Our boutiques are listed below.';
    placeholder.appendChild(msg);
    mapEl.appendChild(placeholder);
  };

  window._nhMapReady = function() {
    var mapEl = document.getElementById('nh-locator-map-' + sectionId);
    if (!mapEl || !window.google) return;

    // Remove placeholder
    var placeholder = mapEl.querySelector('.nh-locator__map-placeholder');
    if (placeholder) placeholder.remove();

    window._nhMap = new google.maps.Map(mapEl, {
      center: { lat: 39.8, lng: -98.5 },
      zoom: 4,
      disableDefaultUI: true,
      zoomControl: true,
      /* 2026-09-02: zooming, panning and marker clicks were all dead
         because the container carried `inert`. That is now removed and
         the map is a labelled interactive region, so its own controls
         are enabled again.

         gestureHandling 'cooperative' keeps the page in charge of the
         scroll: on desktop the wheel scrolls the page and only zooms
         the map with cmd or ctrl held, and on touch one finger scrolls
         the page while two fingers pan and pinch the map. The map can
         therefore never trap the customer mid-page.

         keyboardShortcuts true restores arrow-key panning and +/- zoom
         for keyboard users (WCAG 2.1.1). The store cards below stay as
         the text-equivalent path. */
      gestureHandling: 'cooperative',
      keyboardShortcuts: true,
      styles: [
        { featureType: 'all', elementType: 'geometry', stylers: [{ saturation: -100 }] },
        { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
        { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f3ef' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#dedede' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] }
      ]
    });

    window._nhMarkers = [];

    // Shared InfoWindow instance (only one open at a time)
    var infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
    window._nhInfoWindow = infoWindow;

    locations.forEach(function(loc) {
      if (!loc.lat || !loc.lng) return;
      var slug = loc.slug;
      if (!slug) return;
      var markerLat = Number(loc.lat);
      var markerLng = Number(loc.lng);
      var marker = new google.maps.Marker({
        position: { lat: markerLat, lng: markerLng },
        map: window._nhMap,
        title: loc.name || '',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#dd4444',
          fillOpacity: 1,
          strokeWeight: 1,
          strokeColor: '#ffffff'
        }
      });
      marker._nhCountry = slug;
      marker._nhLat = markerLat;
      marker._nhLng = markerLng;
      marker._nhLocData = loc;
      window._nhMarkers.push(marker);

      // ── Pin click: show full store card in InfoWindow on the map ──
      marker.addListener('click', function() {
        infoWindow.setContent(buildInfoWindowCard(loc));
        infoWindow.open(window._nhMap, marker);
      });
    });

    // Show only pins for active tab
    var activeTab = document.querySelector('.nh-locator__tab.is-active');
    if (activeTab) centerMap(activeTab.dataset.nhTab);
  };

  function centerMap(slug) {
    if (!window._nhMap || !window._nhMarkers) return;

    // Show/hide markers by country
    window._nhMarkers.forEach(function(marker) {
      marker.setVisible(marker._nhCountry === slug);
    });

    // Frame the map around that country's own pins rather than a hardcoded
    // centre and zoom per country (the old slugToCenter table, which meant a
    // country nobody had entered coordinates for simply never got framed).
    // fitMapToVisible already caps the zoom, so a single boutique does not
    // land at street level.
    fitMapToVisible(null);
  }

  /**
   * Escapes HTML entities in a string for safe insertion into innerHTML.
   */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Second address line, formatted per the country's addressFormat:
   *   us   → City, State Zip
   *   city → City          (no postal code)
   *   eu   → Zip City      (default)
   * Previously a hardcoded France / Switzerland / Hong Kong check, which gave
   * every other country a US-shaped address.
   */
  function addressLine2(loc) {
    var city  = escapeHtml(loc.city  || '');
    var state = escapeHtml(loc.state || '');
    var zip   = escapeHtml(loc.zip   || '');

    switch (configFor(loc).addressFormat) {
      case 'us':
        return city + (state ? ', ' + state : '') + (zip ? ' ' + zip : '');
      case 'city':
        return city;
      default:
        return (zip ? zip + ' ' : '') + city;
    }
  }

  /**
   * Formats a phone number using the country's display pattern, where each
   * "#" takes one digit. The number of "#" is the expected digit count, so a
   * trunk prefix typed by a colleague (French leading 0, US leading 1) is
   * trimmed. Falls back to the number exactly as typed.
   *
   * Mirrors the same logic in snippets/nh-store-card.liquid.
   */
  /**
   * The national significant number: the digits as typed, with any trunk
   * prefix or country code trimmed off the front, per the pattern's slot
   * count. Returns null when the number does not fit the pattern.
   */
  function nationalDigits(loc) {
    var digits = (loc.phone || '').replace(/\D/g, '');
    var pattern = configFor(loc).phonePattern || '';
    if (!digits || !pattern) return null;

    var slots = (pattern.match(/#/g) || []).length;
    if (digits.length > slots) digits = digits.slice(digits.length - slots);
    return digits.length === slots ? digits : null;
  }

  function formatPhone(loc) {
    var phone = loc.phone || '';
    if (!phone) return '';

    var digits = nationalDigits(loc);
    if (!digits) return escapeHtml(phone);

    var cursor = 0;
    return escapeHtml((configFor(loc).phonePattern || '').replace(/#/g, function() {
      return digits.charAt(cursor++);
    }));
  }

  /**
   * tel: href for a location. Uses the dialling prefix plus the national
   * number with its trunk prefix removed, so a Paris number typed as
   * "01 42 60 30 30" links as +33142603030 and not +330142603030.
   * With no prefix in the registry the digits are linked as typed, without
   * a "+", rather than guessing a country.
   */
  function telHref(loc) {
    var code = configFor(loc).phone || '';
    var digits = nationalDigits(loc) || (loc.phone || '').replace(/\D/g, '');
    return code ? 'tel:+' + code + digits : 'tel:' + digits;
  }

  /**
   * Builds a dark typographic store card for the Google Maps InfoWindow.
   * No image. White text on #333 background. Uses theme font classes.
   *
   * WCAG 2.0 AA compliant:
   *   - min 44px touch targets on all interactive elements (2.5.5)
   *   - #ffffff on #333333 = 12.6:1 contrast (1.4.3)
   *   - visually-hidden context for unique link purpose (2.4.4)
   *   - lang attribute for French content (3.1.2)
   */
  function buildInfoWindowCard(loc) {
    var name = escapeHtml(loc.name || '');
    var lat = Number(loc.lat);
    var lng = Number(loc.lng);
    var booking = loc.booking || '';

    // WCAG 3.1.2 — mark the store's own text with its language when that
    // differs from the page. Driven by the registry rather than a hardcoded
    // list of French-speaking countries.
    var contentLang = configFor(loc).lang || '';
    var pageLang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    var langAttr = (contentLang && contentLang !== pageLang) ? ' lang="' + contentLang + '"' : '';

    var formattedPhone = formatPhone(loc);
    var addrLine1 = escapeHtml(loc.address || '');
    var addrLine2 = addressLine2(loc);

    // Visually-hidden utility
    var sr = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';

    // ── Card uses CSS classes defined in the section <style> for theme fonts ──
    var c = '<div class="nh-iw"' + langAttr + ' role="article" aria-label="' + name + '">';

    // Store name as h3
    c += '<h3 class="nh-iw__name">' + name + '</h3>';

    // Address as p
    c += '<p class="nh-iw__address">' + addrLine1 + '<br>' + addrLine2 + '</p>';

    // i18n labels (with English fallbacks) ── read from section dataset
    var iwDirections   = sectionEl.dataset.iwDirections || 'Directions';
    var iwBook         = sectionEl.dataset.iwBook       || 'Book';
    var iwPhoneSr      = template('iwPhoneSr',      'Phone for __NAME__: ',                         { '__NAME__': name });
    var iwDirectionsSr = template('iwDirectionsSr', ' to __NAME__ (opens in new tab)',              { '__NAME__': name });
    var iwBookSr       = template('iwBookSr',       ' appointment at __NAME__ (opens in new tab)',  { '__NAME__': name });

    // Phone as p
    if (formattedPhone) {
      c += '<p class="nh-iw__phone">'
        + '<a href="' + telHref(loc) + '" class="nh-iw__phone-link">'
        + '<span style="' + sr + '">' + iwPhoneSr + '</span>' + formattedPhone
        + '</a></p>';
    }

    // Text links
    c += '<div class="nh-iw__links">';
    c += '<a href="https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng + '"'
      + ' target="_blank" rel="noopener noreferrer" class="nh-iw__link">'
      + iwDirections + '<span style="' + sr + '">' + iwDirectionsSr + '</span></a>';

    if (booking) {
      c += '<a href="' + escapeHtml(booking) + '"'
        + ' target="_blank" rel="noopener noreferrer" class="nh-iw__link">'
        + iwBook + '<span style="' + sr + '">' + iwBookSr + '</span></a>';
    }

    c += '</div>';
    c += '</div>';
    return c;
  }

  initMap();

  // ── Concierge FrontChat ───────────────────────────────────────────────────

  var chatBtn = document.querySelector('[data-nh-locator-chat]');
  if (chatBtn) {
    chatBtn.addEventListener('click', function() {
      // WCAG 2.1.1 (#35) — if FrontChat hasn't loaded, fall back to the
      // contact page so keyboard / AT users always get a response.
      if (typeof window.FrontChat === 'function') {
        window.FrontChat('show');
      } else {
        window.location.href = (window.NH_ROUTES && window.NH_ROUTES.contact) || '/pages/contact';
      }
    });
  }

  // ── Phone number formatting ────────────────────────────────────────────────
  //
  // 2026-09-09: removed. The grid cards used to be rendered with the raw
  // number and rewritten here on load, using a per-country if/else chain that
  // defaulted every unlisted country to +1. The number is now formatted in
  // Liquid from the registry's display pattern, so it is correct in the HTML
  // before any JavaScript runs. formatPhone() above does the same job for the
  // map InfoWindow, which is built client-side.

  // ── Progressive disclosure: "View more" for panels with many cards ────────
  //
  // Shows the first INITIAL_VISIBLE cards per country panel. If there are
  // more, a "View more locations" button appears. Clicking it reveals
  // all remaining cards. Searching or filtering bypasses this and shows
  // all matching results directly.

  var INITIAL_VISIBLE = 12;

  function initViewMore() {
    panels.forEach(function(panel) {
      // Skip the online boutiques panel
      if (panel.id.indexOf('-online-') !== -1) return;

      var cards = panel.querySelectorAll('[data-nh-card]');
      if (cards.length <= INITIAL_VISIBLE) return;

      // Hide cards beyond the initial set
      cards.forEach(function(card, i) {
        if (i >= INITIAL_VISIBLE) {
          card.classList.add('nh-locator__card--deferred');
          card.style.display = 'none';
        }
      });

      // Create the "View more" button (uses translatable label from Liquid).
      // It is a one-way "load more" control that is removed once used, so it
      // carries no aria-expanded state; focus moves to the first revealed card.
      var remaining = cards.length - INITIAL_VISIBLE;
      var grid = panel.querySelector('.nh-locator__grid');
      var btn = document.createElement('button');
      btn.className = 'nh-locator__view-more';
      btn.setAttribute('type', 'button');
      btn.textContent = template('viewMoreLabel', 'View __COUNT__ more locations', {
        '__COUNT__': remaining
      });
      btn.addEventListener('click', function() {
        // Capture the first deferred card before revealing so we can move
        // focus to it after the trigger button is removed (WCAG 2.4.3 Focus Order).
        var firstRevealed = panel.querySelector('.nh-locator__card--deferred');
        panel.querySelectorAll('.nh-locator__card--deferred').forEach(function(card) {
          card.classList.remove('nh-locator__card--deferred');
          card.style.display = '';
        });
        btn.remove();
        if (firstRevealed) {
          // Make the card programmatically focusable and move focus to it
          // so keyboard users land on the newly revealed content.
          if (!firstRevealed.hasAttribute('tabindex')) {
            firstRevealed.setAttribute('tabindex', '-1');
          }
          firstRevealed.focus({ preventScroll: false });
        }
      });

      if (grid) {
        grid.parentNode.insertBefore(btn, grid.nextSibling);
      }
    });
  }

  initViewMore();

})();